import { useEffect, useState, useMemo } from 'react';
import { continueRender, delayRender, useCurrentFrame } from 'remotion';
import CaptionsPage from './CaptionsPage';
import { CAPTION_PAGES_FRAME_BUFFER } from '../../../../player-config/src/constants/Remotion.constants';
import { getComputedCaptionsStyles } from './utils';
import { Page, RemotionConfig } from '@/player-config/src/types/remotion-types';
import { approxGTE, approxLTE } from '@/player-config/src/utils/compareUtils';
import { loadRemotionFont } from '@/libs/fonts';
import type { Font } from '@/player-config/src/types/RemotionPlayerConfigTypes/Font';

export default function CaptionsPages({
  pages,
  onOutlineRelease,
  drift,
  canEditCaptions,
  font,
  disableCaptionsDragging = false,
  disableCaptionsResize = false,
  onOutlineResize
}: Pick<RemotionConfig, 'onOutlineRelease' | 'drift' | 'canEditCaptions' | 'onOutlineResize'> & {
  pages: Page[];
  font?: Font;
  disableCaptionsDragging?: boolean;
  disableCaptionsResize?: boolean;
}) {
  const [handle] = useState(() => delayRender('Loading Assets...', { retries: 3 }));
  const frame = useCurrentFrame();

  const visiblePages = useMemo(() => {
    return pages.filter(page => {
      const pageStart = page.fromFrame - CAPTION_PAGES_FRAME_BUFFER;
      const pageEnd = page.toFrame + CAPTION_PAGES_FRAME_BUFFER;
      const currentFrame = frame - drift;

      return approxGTE(currentFrame, pageStart) && approxLTE(currentFrame, pageEnd) && !page.isHidden;
    });
  }, [pages, frame, drift]);

  const hasPages = useMemo(() => pages.length > 0, [pages.length]);

  const computedCaptionsStyles = useMemo(() => {
    return hasPages ? getComputedCaptionsStyles(pages[0]) : { groupedTokensStyle: {} };
  }, [hasPages, pages]);

  useEffect(() => {
    if (font?.location && font.name) {
      loadRemotionFont(font)
        .then(() => {
          // eslint-disable-next-line no-console
          console.log('Font loaded - ', font.name);
        })
        .catch(error => {
          // eslint-disable-next-line no-console
          console.error('Error loading font - ', font.name, font.location, error);
        })
        .finally(() => {
          continueRender(handle);
        });
    } else {
      continueRender(handle);
    }
  }, [font?.location, font?.name]);

  return visiblePages.map(page => (
    <CaptionsPage
      key={`${page.fromFrame}-${page.toFrame}`}
      page={page}
      onOutlineRelease={onOutlineRelease}
      canEditCaptions={canEditCaptions}
      drift={drift}
      disableCaptionsDragging={disableCaptionsDragging}
      disableCaptionsResize={disableCaptionsResize}
      onOutlineResize={onOutlineResize}
      computedCaptionsStyles={computedCaptionsStyles}
    />
  ));
}
